import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { postingAge, remoteLabel, companyClassMeta, salaryLabel, tjmLabel } from "@/lib/format";
import ScoreRing from "@/components/ScoreRing";
import DecisionMakers from "@/components/DecisionMakers";
import JobDescription from "@/components/JobDescription";
import UpgradeModal from "@/components/UpgradeModal";
import OutreachDrawer from "@/components/crm/OutreachDrawer";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import { ActivityTimeline } from "@/components/crm/ActivityTimeline";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Buildings, MapPin, CaretDown, Broadcast, UserPlus, Target, TrendUp,
  Sparkle, ArrowSquareOut, CheckCircle, X, UsersThree, MagnifyingGlassPlus, PaperPlaneTilt, CurrencyEur,
} from "@phosphor-icons/react";

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [showTech, setShowTech] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selConsultant, setSelConsultant] = useState("");
  const [fetchingDesc, setFetchingDesc] = useState(false);
  const [showOutreach, setShowOutreach] = useState(false);
  const [timelineKey, setTimelineKey] = useState(0);

  const isPaid = !!user?.entitlements?.is_paid;
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/opportunities/${id}`);
      setData(data);
      setAssignments(data.assignments || []);
    } catch { /* noop */ }
    try {
      const { data: cs } = await api.get("/consultants");
      setConsultants(cs);
    } catch { /* noop */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const assign = async () => {
    if (!selConsultant) { toast.error("Sélectionnez un consultant."); return; }
    try {
      const { data } = await api.post(`/opportunities/${id}/assign`, { consultant_id: selConsultant });
      setAssignments((a) => [...a, data]);
      setSelConsultant("");
      toast.success("Consultant positionné sur cette opportunité");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const unassign = async (aid) => {
    try {
      await api.delete(`/assignments/${aid}`);
      setAssignments((a) => a.filter((x) => x.id !== aid));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const fetchFullDesc = async () => {
    setFetchingDesc(true);
    try {
      const { data: res } = await api.post(`/admin/opportunities/${id}/fetch-full-description`);
      if (res.updated) {
        setData((d) => ({ ...d, opportunity: { ...d.opportunity, description: res.description, skills: res.skills || d.opportunity.skills } }));
        setShowFullDesc(true);
        toast.success("Texte complet récupéré via la recherche marché");
      } else {
        toast.info(res.message || "Aucune description plus complète trouvée.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setFetchingDesc(false); }
  };

  if (!data) return <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>;

  const { opportunity: o, company, company_intel: intel } = data;
  const score = state?.score;
  const oppScore = state?.opportunity_score;
  const reasons = state?.match_reasons || [];
  const dist = state?.distance_km;
  const allSkills = [...(o.skills || []), ...(o.technologies || [])];
  const shownSkills = showAllSkills ? allSkills : allSkills.slice(0, 8);
  const remote = remoteLabel(o.remote_status);
  const age = postingAge(o.posted_at);
  const tjmTxt = tjmLabel(o.tjm);
  const sal = tjmTxt ? { label: tjmTxt, kind: "tjm" } : salaryLabel(o);
  const fullDesc = o.description || "";
  const isLongDesc = fullDesc.length > 600;
  const shownDesc = showFullDesc || !isLongDesc ? fullDesc : fullDesc.slice(0, 600) + "…";

  const assignedIds = new Set(assignments.map((a) => a.consultant_id));
  const availableConsultants = consultants.filter((c) => !assignedIds.has(c.id));

  const tech = [
    ["Source", o.source], ["Type de contrat", o.contract_type], ["Contrat", o.employment_type],
    ["Salaire", o.salary_min ? `${o.salary_min}${o.salary_max ? "–" + o.salary_max : ""} ${o.salary_currency || ""}` : null],
    ["Département", o.department], ["Région", o.region], ["Pays", o.country], ["Famille métier", o.job_family],
    ["Expérience", o.years_experience_min ? `${o.years_experience_min}${o.years_experience_max ? "–" + o.years_experience_max : ""} ans` : null],
    ["Langues", (o.languages || []).join(", ")], ["Statut", o.status], ["Publié le", o.posted_at],
    ["Vu en premier", o.first_seen_at], ["Réf. externe", o.external_reference], ["ID source", o.source_job_id],
  ].filter(([, v]) => v);

  return (
    <div className="max-w-6xl">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="flex items-center gap-2 text-sm text-muted hover:text-fg mb-6">
        <ArrowLeft size={16} /> Retour aux opportunités
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* SECTION 1 — Essentiel */}
          <div className="surface rounded-lg p-6" data-testid="opportunity-detail">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="overline mb-2 flex items-center gap-2"><Broadcast size={13} className="text-accent" /> Signal de recrutement</div>
                {data.positioning && (
                  <div className="mb-2" data-testid="detail-commercial-status">
                    <CommercialStatusBadge positioning={data.positioning} opportunityId={id} companyName={company.name} />
                  </div>
                )}
                <h1 className="font-display text-3xl font-bold">{o.role_title}</h1>
                <div className="text-muted mt-1">{company.name}</div>
              </div>
              {score != null && <ScoreRing score={score} size={64} />}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted font-mono mt-4">
              <span className="flex items-center gap-1.5"><MapPin size={14} /> {o.city || "—"}{dist != null ? ` · ${dist} km` : ""}</span>
              <span>{o.seniority}</span>
              {(() => {
                const ccm = companyClassMeta(o.company_class || (o.client_type === "intermediary" ? "ESN_CONSULTING" : "FINAL_CLIENT"));
                return (
                  <span className="flex items-center gap-1 rounded px-1.5 py-0.5 border font-semibold" data-testid="company-class-tag"
                    style={{ color: ccm.color, borderColor: ccm.color + "66", background: ccm.color + "14" }}>
                    {ccm.label}
                  </span>
                );
              })()}
              {remote && <span>{remote}</span>}
              {sal && (
                <span className="flex items-center gap-1 font-semibold rounded px-1.5 py-0.5 border"
                  style={{ color: "#34D399", borderColor: "#34D39955", background: "#34D39914" }}
                  data-testid="tjm-badge"
                  title={sal.kind === "tjm" ? "Tarif journalier moyen (TJM)" : "Rémunération indiquée dans l'annonce"}>
                  <CurrencyEur size={13} weight="fill" /> {sal.label}
                </span>
              )}
              {age && <span>{age}</span>}
              {oppScore != null && <span className="text-accent flex items-center gap-1"><Target size={13} /> Opportunité {oppScore}/100</span>}
            </div>
          </div>

          {/* SECTION 2 — Pourquoi */}
          {reasons.length > 0 && (
            <div className="surface rounded-lg p-6" data-testid="match-reasons">
              <div className="overline mb-3 flex items-center gap-2"><Sparkle size={13} className="text-accent" weight="fill" /> Pourquoi ce signal correspond</div>
              <p className="text-sm text-fg/90 mb-3">Correspondance {score >= 80 ? "très forte" : score >= 55 ? "solide" : "partielle"} avec votre recherche.</p>
              <div className="flex flex-wrap gap-2">
                {reasons.map((r, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs font-mono bg-accent/12 text-accent border border-accent/25 px-2.5 py-1 rounded">
                    <CheckCircle size={12} weight="fill" /> {r.label} {r.status === "exact" ? "→ exact" : ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 3 — Compétences */}
          <div className="surface rounded-lg p-6">
            <div className="overline mb-3">Compétences recherchées</div>
            <div className="flex flex-wrap gap-1.5">
              {shownSkills.map((s, i) => (
                <span key={i} className="text-xs font-mono bg-surface-2 text-fg/80 border border-border px-2.5 py-1 rounded">{s}</span>
              ))}
            </div>
            {allSkills.length > 8 && (
              <button onClick={() => setShowAllSkills((v) => !v)} data-testid="toggle-skills" className="text-xs text-accent mt-3 hover:underline">
                {showAllSkills ? "Réduire" : `Voir toutes les compétences (${allSkills.length})`}
              </button>
            )}
          </div>

          {/* SECTION — Métiers proches (élargir le placement) */}
          {(data.related_roles || []).length > 0 && (
            <div className="surface rounded-lg p-6" data-testid="related-roles">
              <div className="overline mb-2 flex items-center gap-2"><Target size={13} className="text-[#7C5CFF]" weight="fill" /> Aussi ouvert à…</div>
              <p className="text-xs text-muted mb-3">
                {data.canonical_role?.name ? <>Métier détecté : <span className="text-fg/85">{data.canonical_role.name}</span>. </> : null}
                Positionnez aussi un consultant sur ces métiers proches — plus d'opportunités en une seule recherche.
              </p>
              <div className="flex flex-wrap gap-2">
                {(data.related_roles || []).map((r) => (
                  <span key={r.code} data-testid={`related-role-${r.code}`}
                    className="text-xs bg-[#7C5CFF]/10 text-[#b9a6ff] border border-[#7C5CFF]/30 px-2.5 py-1 rounded-md">
                    {r.name_fr}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 4 — Besoin détecté */}
          <div className="surface rounded-lg p-6">
            <div className="overline mb-3">Besoin détecté</div>
            <JobDescription text={shownDesc} testid="need-summary" />
            {isLongDesc && (
              <button onClick={() => setShowFullDesc((v) => !v)} data-testid="toggle-desc" className="text-xs text-accent mt-3 hover:underline">
                {showFullDesc ? "Réduire" : "Voir la description complète"}
              </button>
            )}
            {isAdmin && fullDesc.length < 500 && (
              <div className="mt-4 pt-3 border-t border-border/60 flex items-center gap-3 flex-wrap">
                <p className="text-xs text-muted flex-1 min-w-[200px]">Cette source ne fournit qu'un résumé. Récupérez le texte intégral de l'offre.</p>
                <button onClick={fetchFullDesc} disabled={fetchingDesc} data-testid="fetch-full-desc-btn"
                  className="flex items-center gap-2 text-xs font-semibold border border-accent/40 text-accent px-3 py-1.5 rounded-md hover-lift disabled:opacity-60 shrink-0">
                  <MagnifyingGlassPlus size={14} /> {fetchingDesc ? "Récupération…" : "Récupérer le texte complet"}
                </button>
              </div>
            )}
          </div>

          {/* SECTION — Informations complémentaires (repliée) */}
          <div className="surface rounded-lg overflow-hidden">
            <button onClick={() => setShowTech((v) => !v)} data-testid="toggle-tech"
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-surface-2/40">
              <span className="overline !text-fg/70">Informations complémentaires</span>
              <CaretDown size={16} className={`text-muted transition-transform ${showTech ? "rotate-180" : ""}`} />
            </button>
            {showTech && (
              <div className="px-6 pb-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="tech-info">
                {tech.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-border/50 py-1.5">
                    <span className="text-muted">{k}</span>
                    <span className="text-fg/90 font-mono text-xs text-right truncate max-w-[60%]">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {o.source && (
            <div className="text-xs text-muted font-mono flex items-center gap-2" data-testid="provenance">
              Source : {o.source}
              {o.source_url && <a href={o.source_url.startsWith("http") ? o.source_url : `https://${o.source_url}`} target="_blank" rel="noreferrer" className="text-accent hover:underline flex items-center gap-1">· Voir la source originale <ArrowSquareOut size={11} /></a>}
            </div>
          )}
        </div>

        {/* Aside: company intel + consultant assignment */}
        <aside className="space-y-6">
          <div className="surface rounded-lg p-6">
            <div className="w-11 h-11 rounded-md flex items-center justify-center mb-3" style={{ background: `${company.logo_color}1a`, border: `1px solid ${company.logo_color}40` }}>
              <Buildings size={22} weight="duotone" style={{ color: company.logo_color }} />
            </div>
            <div className="overline mb-1">{company.sector || "Entreprise"}</div>
            <h2 className="font-display text-lg font-bold mb-3">{company.name}</h2>
            {intel && (
              <div className="space-y-2 text-sm border-t border-border pt-3" data-testid="company-intel">
                <IntelRow icon={Broadcast} label="Besoins IT actifs" value={intel.active_signals} />
                <IntelRow icon={Sparkle} label="Nouveaux cette semaine" value={intel.new_this_week} accent />
                <IntelRow icon={Target} label="Besoins Cloud / DevOps" value={intel.cloud_devops} />
                <IntelRow icon={TrendUp} label="Activité recrutement" value={`+${intel.activity_trend}%`} accent />
              </div>
            )}
            <Link to={`/app/company/${company.id}`} data-testid="view-company-btn"
              className="mt-4 w-full flex items-center justify-center gap-2 text-sm border border-border rounded-md px-4 py-2.5 hover-lift hover:border-accent/40 text-fg">
              Voir l'entreprise
            </Link>
          </div>

          {/* Consultant assignment */}
          <div className="surface rounded-lg p-6" data-testid="assignment-card">
            <div className="overline mb-3 flex items-center gap-2"><UsersThree size={14} className="text-accent" /> Positionner un consultant</div>
            <button onClick={() => setShowOutreach(true)} data-testid="outreach-cta"
              className="w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold px-4 py-2.5 rounded-md hover-lift mb-4">
              <PaperPlaneTilt size={16} weight="fill" /> Positionner & contacter
            </button>
            <div className="overline mb-2 !text-muted/70">Positionnement rapide</div>
            {assignments.length > 0 && (
              <div className="space-y-2 mb-3" data-testid="assignments-list">
                {assignments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 bg-surface-2 border border-border rounded-md px-3 py-2" data-testid={`assignment-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-fg font-medium truncate">{a.consultant_name}</div>
                      {a.consultant_role && <div className="text-xs text-muted truncate">{a.consultant_role}</div>}
                    </div>
                    <button onClick={() => unassign(a.id)} className="p-1 text-muted hover:text-destructive shrink-0" data-testid={`unassign-${a.id}`}><X size={15} /></button>
                  </div>
                ))}
              </div>
            )}
            {consultants.length === 0 ? (
              <p className="text-xs text-muted">Ajoutez d'abord vos consultants disponibles depuis l'onglet <Link to="/app/consultants" className="text-accent hover:underline">Consultants</Link>.</p>
            ) : availableConsultants.length === 0 ? (
              <p className="text-xs text-muted">Tous vos consultants sont déjà positionnés sur cette opportunité.</p>
            ) : (
              <div className="flex items-center gap-2">
                <Select value={selConsultant} onValueChange={setSelConsultant}>
                  <SelectTrigger className="flex-1 h-9 bg-surface-2 border-border text-xs" data-testid="assign-select"><SelectValue placeholder="Choisir un consultant" /></SelectTrigger>
                  <SelectContent>
                    {availableConsultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.role || c.seniority}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={assign} data-testid="assign-btn" className="flex items-center gap-1 bg-accent text-black text-sm font-semibold px-3 py-2 rounded-md hover-lift shrink-0">
                  <UserPlus size={15} /> Positionner
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Decision makers — full width */}
      <div className="mt-8">
        <DecisionMakers signalId={id} isPaid={isPaid} isAdmin={isAdmin} onUnlock={() => setShowUpgrade(true)} />
      </div>

      {/* Activity timeline */}
      <div className="mt-8">
        <ActivityTimeline opportunityId={id} refreshKey={timelineKey} title="Activité de cette opportunité" />
      </div>

      <OutreachDrawer
        open={showOutreach} onOpenChange={setShowOutreach}
        signalId={id} companyId={company.id} companyName={company.name}
        roleTitle={o.role_title} city={o.city}
        onSent={() => { setTimelineKey((k) => k + 1); load(); }}
      />

      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} returnTo={pathname} />
    </div>
  );
}

function IntelRow({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted flex items-center gap-2"><Icon size={14} className={accent ? "text-accent" : "text-muted"} /> {label}</span>
      <span className={`font-mono font-bold ${accent ? "text-accent" : "text-fg"}`}>{value}</span>
    </div>
  );
}
